import React, { useEffect, useMemo, useLayoutEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useProject } from 'components/hooks';
import { HeadlinePage } from 'components/common';
import { useTranslation } from 'react-i18next';
import { projectPolicies } from 'helpers';
import { useStyle } from './styles';
import { Input } from 'components/layout';
import {
  Paper,
  Skeleton,
  Grid,
  CircularProgress,
  Backdrop,
  Button,
  Typography
} from '@mui/material';
import { ROUTES } from 'consts';

export default function ProjectFormDumb({ defaultValues, handleOnSubmit, isEdit }) {
  const { isLoaded, fieldData, fieldDataConst, getFieldData } = useProject();
  const [t] = useTranslation();
  const classes = useStyle();
  const methods = useForm({
    defaultValues: useMemo(() => {
      return defaultValues;
    }, [defaultValues])
  });
  const { handleSubmit, reset, control, setValue, watch } = methods;
  
  const scmType = watch('scmType');

  const fields = [
    { policy: projectPolicies.name },
    { policy: projectPolicies.description },
    {
      policy: projectPolicies.company,
      options: fieldData.companies,
      isLoaded: isLoaded.fieldData
    },
    { policy: projectPolicies.scmType, options: fieldDataConst.scmType  },
  ];

  // Dynamically add fields based on SCM Type
  if (scmType === 'Manual' || scmType === 'manual') {
    fields.push({ policy: projectPolicies.folder });
  } else if (scmType === 'Git' || scmType === 'git') {
    fields.push({ policy: projectPolicies.folder });
    fields.push({ policy: projectPolicies.scmUrl });
    fields.push({ policy: projectPolicies.scmBranch });
    fields.push({ policy: projectPolicies.scmRefspec });
    fields.push({ 
        policy: projectPolicies.credential,
        options: fieldData.credentials, // Assuming credentials are loaded in fieldData
        isLoaded: isLoaded.fieldData
    });
    fields.push({ policy: projectPolicies.clean });
    fields.push({ policy: projectPolicies.deleteOnUpdate });
    fields.push({ policy: projectPolicies.allowBranchOverride });
  }

  useLayoutEffect(() => {
    getFieldData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setValue('company', !fieldData.companies.length ? '' : fieldData.companies[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldData]);

  useEffect(() => {
    if (isEdit) reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  return (
    <>
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoaded.post || isLoaded.put}
      >
        <CircularProgress color="primary" />
      </Backdrop>

      <HeadlinePage title={isEdit ? 'projectEdit.headline' : 'projectCreate.headline'} goBack={ROUTES.PROJECT_LIST}/>
      <FormProvider { ...methods }>
        <Paper className={classes.paperStyle} >
          <form onSubmit={handleSubmit(handleOnSubmit)}>

            <Typography variant="h6" sx={{ padding: '8px 0px' }}>
              {t('projectList.form.title')}
            </Typography>

            <Grid container spacing={2}>
              {fields.map((field) => (
                <React.Fragment key={field.policy.id}>
                  {!field?.isLoaded ? (
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                      <Input
                        {...field.policy}
                        control={control}
                        options={field.options}
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Skeleton
                        sx={{ marginBottom: 1 }}
                        variant="text"
                        animation="wave"
                        width="60%"
                      />
                      <Skeleton height="40px" variant="rounded" animation="wave" />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Grid>


            <Grid container sx={{ mt: 4 }}>
              <Grid item md={2} lg={3} />
              <Grid item xs={12} sm={12} md={8} lg={6}>
                <Button variant="contained" sx={{ width: '100%' }} type='submit'>
                  {isEdit ? t('projectEdit.components.submit') : t('projectCreate.components.submit')}
                </Button>
              </Grid>
              <Grid item md={2} lg={3} />
            </Grid>
          </form>
        </Paper>
      </FormProvider>
    </>
  );
}
